import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/api';

interface ThreatActor {
  id: number;
  code: string;
  name: string;
  nameTranslations: Record<string, string>;
  description?: string;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    articleAnalyses: number;
    dailyRiskScores: number;
  };
}

interface ActorFormData {
  code: string;
  name: string;
  nameTranslations: {
    en: string;
    et: string;
    ru: string;
  };
  description: string;
  color: string;
  isActive: boolean;
}

export function ActorsTab() {
  const { isAuthenticated } = useAuth();
  const [actors, setActors] = useState<ThreatActor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingActor, setEditingActor] = useState<ThreatActor | null>(null);
  const [showAddActor, setShowAddActor] = useState(false);

  // Form state
  const [formData, setFormData] = useState<ActorFormData>({
    code: '',
    name: '',
    nameTranslations: {
      en: '',
      et: '',
      ru: '',
    },
    description: '',
    color: '#dc2626',
    isActive: true,
  });

  // Predefined color options
  const colorOptions = [
    { value: '#dc2626', label: 'Red', preview: 'bg-red-600' },
    { value: '#ea580c', label: 'Orange', preview: 'bg-orange-600' },
    { value: '#ca8a04', label: 'Amber', preview: 'bg-amber-600' },
    { value: '#16a34a', label: 'Green', preview: 'bg-green-600' },
    { value: '#0891b2', label: 'Cyan', preview: 'bg-cyan-600' },
    { value: '#2563eb', label: 'Blue', preview: 'bg-blue-600' },
    { value: '#7c3aed', label: 'Violet', preview: 'bg-violet-600' },
    { value: '#c026d3', label: 'Fuchsia', preview: 'bg-fuchsia-600' },
    { value: '#6b7280', label: 'Gray', preview: 'bg-gray-600' },
    { value: '#000000', label: 'Black', preview: 'bg-black' },
  ];

  useEffect(() => {
    if (isAuthenticated) {
      fetchActors();
    }
  }, [isAuthenticated]);

  const fetchActors = async () => {
    try {
      setLoading(true);
      const actors = await apiService.getThreatActors();
      setActors(actors);
      setError(null);
    } catch (err) {
      setError('Failed to fetch threat actors');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      nameTranslations: {
        en: '',
        et: '',
        ru: '',
      },
      description: '',
      color: '#dc2626',
      isActive: true,
    });
  };

  const handleEdit = (actor: ThreatActor) => {
    setEditingActor(actor);
    setFormData({
      code: actor.code,
      name: actor.name,
      nameTranslations: {
        en: actor.nameTranslations['en'] || actor.name,
        et: actor.nameTranslations['et'] || '',
        ru: actor.nameTranslations['ru'] || '',
      },
      description: actor.description || '',
      color: actor.color,
      isActive: actor.isActive,
    });
    setShowAddActor(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        nameTranslations: {
          en: formData.nameTranslations.en || formData.name,
          et: formData.nameTranslations.et,
          ru: formData.nameTranslations.ru,
        },
      };

      if (editingActor) {
        await apiService.updateThreatActor(editingActor.id, payload);
      } else {
        await apiService.createThreatActor(payload);
      }

      setShowAddActor(false);
      setEditingActor(null);
      resetForm();
      await fetchActors();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save threat actor');
    }
  };

  const handleDelete = async (actor: ThreatActor) => {
    const usageCount = (actor._count?.articleAnalyses || 0) + (actor._count?.dailyRiskScores || 0);
    
    if (usageCount > 0) {
      alert(`Cannot delete "${actor.name}". It has been used in ${usageCount} analyses and risk scores.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${actor.name}"? This cannot be undone.`)) return;

    try {
      await apiService.deleteThreatActor(actor.id);
      await fetchActors();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete threat actor');
    }
  };

  const handleToggleActive = async (actor: ThreatActor) => {
    try {
      await apiService.updateThreatActor(actor.id, {
        isActive: !actor.isActive
      });
      await fetchActors();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update threat actor');
    }
  };

  if (loading) {
    return <div className="tc-card p-6">Loading threat actors...</div>;
  }

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-red-600">{error}</div>
        <button onClick={fetchActors} className="tc-btn tc-btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Threat Actors Management */}
      <div className="tc-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold tc-text-primary">Threat Actors</h3>
          <button
            onClick={() => {
              resetForm();
              setEditingActor(null);
              setShowAddActor(true);
            }}
            className="tc-btn tc-btn-primary"
          >
            ➕ Add Actor
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="tc-bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Color
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Translations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="tc-bg-primary divide-y divide-gray-200">
              {actors.map((actor) => (
                <tr key={actor.id} className="tc-hover">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium tc-text-primary">
                        {actor.name} ({actor.code})
                      </div>
                      <div className="text-sm tc-text-secondary">
                        {actor.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-6 h-6 rounded"
                        style={{ backgroundColor: actor.color }}
                      />
                      <span className="text-sm tc-text-secondary">{actor.color}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs space-y-1">
                      <div>EN: {actor.nameTranslations['en'] || actor.name}</div>
                      <div>ET: {actor.nameTranslations['et'] || '-'}</div>
                      <div>RU: {actor.nameTranslations['ru'] || '-'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm tc-text-secondary">
                      <div>{actor._count?.articleAnalyses || 0} analyses</div>
                      <div>{actor._count?.dailyRiskScores || 0} risk scores</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {actor.isActive ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        ✅ Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                        ⏸️ Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(actor)}
                      className="tc-link"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(actor)}
                      className="tc-link"
                    >
                      {actor.isActive ? '⏸️ Deactivate' : '▶️ Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(actor)}
                      className="tc-link text-red-600"
                      disabled={(actor._count?.articleAnalyses || 0) + (actor._count?.dailyRiskScores || 0) > 0}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actor Form Modal */}
      {showAddActor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="tc-card p-6 max-w-2xl max-h-[90vh] overflow-auto">
            <h3 className="text-xl font-semibold tc-text-primary mb-4">
              {editingActor ? 'Edit Threat Actor' : 'Add New Threat Actor'}
            </h3>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Code (Uppercase, no spaces)
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                    className="tc-input w-full"
                    placeholder="RUSSIA"
                    disabled={!!editingActor}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Display Name (English)
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="tc-input w-full"
                    placeholder="Russia"
                  />
                </div>
              </div>

              {/* Translations */}
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  Translations
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs tc-text-secondary">English</label>
                    <input
                      type="text"
                      value={formData.nameTranslations.en}
                      onChange={(e) => setFormData({
                        ...formData,
                        nameTranslations: { ...formData.nameTranslations, en: e.target.value }
                      })}
                      className="tc-input w-full"
                      placeholder="Russia"
                    />
                  </div>
                  <div>
                    <label className="text-xs tc-text-secondary">Estonian</label>
                    <input
                      type="text"
                      value={formData.nameTranslations.et}
                      onChange={(e) => setFormData({
                        ...formData,
                        nameTranslations: { ...formData.nameTranslations, et: e.target.value }
                      })}
                      className="tc-input w-full"
                      placeholder="Venemaa"
                    />
                  </div>
                  <div>
                    <label className="text-xs tc-text-secondary">Russian</label>
                    <input
                      type="text"
                      value={formData.nameTranslations.ru}
                      onChange={(e) => setFormData({
                        ...formData,
                        nameTranslations: { ...formData.nameTranslations, ru: e.target.value }
                      })}
                      className="tc-input w-full"
                      placeholder="Россия"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="tc-input w-full"
                  rows={3}
                  placeholder="Primary threat actor for Estonia..."
                />
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  Display Color
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: option.value })}
                      className={`p-3 rounded border-2 transition-all ${
                        formData.color === option.value 
                          ? 'border-blue-500 shadow-lg scale-105' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-6 h-6 rounded ${option.preview}`} />
                        <span className="text-sm tc-text-secondary">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-2">
                  <label className="text-xs tc-text-secondary">Custom color (hex)</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="tc-input w-32"
                    placeholder="#dc2626"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-3"
                  />
                  <span className="text-sm font-medium tc-text-primary">Active</span>
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowAddActor(false);
                  setEditingActor(null);
                  resetForm();
                }}
                className="tc-btn tc-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="tc-btn tc-btn-primary"
              >
                {editingActor ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}